import React, { useEffect, useState, useCallback } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { authManager } from '../utils/auth';

const Base_Url = process.env.REACT_APP_API_BASE_URL;

// Ticket #14 (fulfillment): fee_credit auto-fulfills via Stripe when the
// member owns their own billing (loyalty.controller.js's redeem()) — this
// queue is for everything that couldn't auto-apply: every gift_card (no
// automated delivery path exists yet — needs a rewards-vendor integration,
// not built), a failed fee_credit auto-apply attempt, or a family-plan
// member's fee_credit (their Premium rides on someone else's billing, so
// there's no safe stripe_customer_id to credit automatically).
const FULFILLMENT_STATUS_BADGE = {
    pending: 'badge-warning',
    fulfilled: 'badge-success',
    failed: 'badge-danger',
};

const FULFILLMENT_STATUS_LABEL = {
    pending: 'Awaiting fulfillment',
    fulfilled: 'Fulfilled',
    failed: 'Auto-apply failed',
};

// membership_unlock (somo-circle1-api#791) never reaches this queue today:
// unlocks live in their own MembershipUnlockRequest collection and need no
// manual fulfilment. Labelled anyway so one that ever does shows readably
// instead of as the raw kind.
const KIND_LABEL = {
    gift_card: 'Gift card',
    fee_credit: 'Membership credit',
    membership_unlock: 'Membership unlock',
};

const formatDollars = (amount) => `$${Number(amount).toFixed(2)}`;

function LoyaltyRedemptions() {
    const [redemptions, setRedemptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({});
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState('pending');
    const [action, setAction] = useState(null); // { redemption } — which redemption is being fulfilled
    const [note, setNote] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [allowed, setAllowed] = useState(null); // null = still checking

    const fetchRedemptions = useCallback(async (targetPage = 1, targetStatus = status) => {
        try {
            setLoading(true);
            const queryParams = new URLSearchParams({
                page: targetPage,
                limit: 20,
                status: targetStatus,
            });
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/loyalty/redemptions?${queryParams}`,
            );
            const data = await res.json();
            if (data.status === 'success') {
                setRedemptions(data.data.redemptions || []);
                setPagination(data.data.pagination || {});
                setPage(targetPage);
            } else {
                toast.error(data.message || 'Failed to load the fulfillment queue.');
            }
        } catch (err) {
            console.error('Error fetching loyalty redemptions:', err);
            toast.error('Error loading the fulfillment queue.');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
        (async () => {
            // Same CircleOne-only, money-affecting gate as LoyaltyRules.js —
            // mirrors the sidebar's showSettings check (role === 'superadmin'
            // || (role === 'admin' && source !== 'mml')). The real
            // authorization boundary is server-side (requireNonMmlAdmin on
            // /admin/loyalty/redemptions and .../fulfill); this only stops an
            // MML-sourced admin from landing on a queue that would 403 on
            // every fetch/fulfill — the sidebar link is already gated the
            // same way, but a bookmark/typed URL bypasses that.
            const currentUser = await authManager.validateUser().catch(() => null);
            const isMmlAdmin = currentUser?.role === 'admin' && currentUser?.source === 'mml';
            if (isMmlAdmin) {
                setAllowed(false);
                setLoading(false);
                return;
            }
            setAllowed(true);
            fetchRedemptions(1, status);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleStatusChange = (e) => {
        const next = e.target.value;
        setStatus(next);
        fetchRedemptions(1, next);
    };

    const openFulfill = (redemption) => {
        setAction({ redemption });
        setNote('');
    };

    const closeAction = () => {
        if (submitting) return;
        setAction(null);
        setNote('');
    };

    const submitFulfill = async () => {
        if (!action) return;
        try {
            setSubmitting(true);
            const res = await authManager.authenticatedFetch(
                `${Base_Url}/admin/loyalty/redemptions/${action.redemption._id}/fulfill`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ note: note.trim() }),
                },
            );
            const data = await res.json();
            if (data.status === 'success') {
                toast.success(data.message || 'Marked fulfilled.');
                setAction(null);
                setNote('');
                fetchRedemptions(page, status);
            } else {
                toast.error(data.message || 'That action failed.');
            }
        } catch (err) {
            console.error('Error fulfilling loyalty redemption:', err);
            toast.error('That action failed.');
        } finally {
            setSubmitting(false);
        }
    };

    if (allowed === false) {
        return (
            <div className="content">
                <div className="container-fluid">
                    <div className="card mb-3">
                        <div className="card-body text-center py-5">
                            <h5>Not available for this admin account.</h5>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <ToastContainer autoClose={3000} />
            <div className="content-header">
                <div className="container-fluid">
                    <div className="row mb-2">
                        <div className="col-sm-6">
                            <h1 className="m-0 txt_lft">Loyalty Redemptions</h1>
                        </div>
                    </div>
                </div>
            </div>

            <div className="content">
                <div className="container-fluid">
                    <div className="card mb-3">
                        <div className="card-body">
                            <div className="row align-items-end">
                                <div className="col-md-3">
                                    <label>Status</label>
                                    <select className="form-control" value={status} onChange={handleStatusChange}>
                                        <option value="pending">Awaiting fulfillment (queue)</option>
                                        <option value="failed">Auto-apply failed</option>
                                        <option value="fulfilled">Fulfilled</option>
                                        <option value="all">All</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-lg-12">
                            <div className="table-responsive">
                                {loading ? (
                                    <div className="text-center p-5">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="sr-only">Loading...</span>
                                        </div>
                                    </div>
                                ) : redemptions.length > 0 ? (
                                    <>
                                        <table width="100%" className="table table-striped table-bordered txt_lft">
                                            <thead>
                                                <tr>
                                                    <th>Member</th>
                                                    <th style={{ width: '100px' }}>Amount</th>
                                                    <th style={{ width: '160px' }}>Redeem as</th>
                                                    <th style={{ width: '150px' }}>Status</th>
                                                    <th style={{ width: '140px' }}>Requested</th>
                                                    <th style={{ width: '160px' }}>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {redemptions.map((r) => (
                                                    <tr key={r._id}>
                                                        <td>
                                                            <span
                                                                className={`fwt_600${r.userId?.email ? ' fast-tooltip' : ''}`}
                                                                data-tooltip={r.userId?.email}
                                                                title={r.userId?.email || undefined}
                                                                aria-label={r.userId?.email ? `${r.userId?.name} ${r.userId?.lastName}, ${r.userId.email}` : undefined}
                                                                tabIndex={r.userId?.email ? 0 : undefined}
                                                            >
                                                                {r.userId?.name} {r.userId?.lastName}
                                                            </span>
                                                        </td>
                                                        <td>{formatDollars(r.dollars)}</td>
                                                        <td>{KIND_LABEL[r.kind] || r.kind}</td>
                                                        <td>
                                                            <span className={`badge ${FULFILLMENT_STATUS_BADGE[r.fulfillmentStatus] || 'badge-secondary'}`}>
                                                                {FULFILLMENT_STATUS_LABEL[r.fulfillmentStatus] || r.fulfillmentStatus}
                                                            </span>
                                                            {r.fulfillmentStatus === 'failed' && r.fulfillmentError && (
                                                                <>
                                                                    <br />
                                                                    <small className="text-danger">{r.fulfillmentError}</small>
                                                                </>
                                                            )}
                                                            {r.fulfillmentStatus === 'fulfilled' && r.fulfillmentRef && (
                                                                <>
                                                                    <br />
                                                                    <small className="text-muted">Ref: {r.fulfillmentRef}</small>
                                                                </>
                                                            )}
                                                        </td>
                                                        <td className='f_sz13'>{new Date(r.createdAt).toLocaleString()}</td>
                                                        <td>
                                                            {r.fulfillmentStatus !== 'fulfilled' ? (
                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-primary"
                                                                    // customstyle.css forces .btn-primary's resting
                                                                    // background to black app-wide (only turns blue
                                                                    // on hover) — overridden here to this app's own
                                                                    // --primary navy-blue token instead of a raw hex,
                                                                    // so it stays in sync if the theme's blue changes.
                                                                    style={{ backgroundColor: 'var(--primary)', borderColor: 'var(--primary)' }}
                                                                    onClick={() => openFulfill(r)}
                                                                >
                                                                    Fulfill
                                                                </button>
                                                            ) : (
                                                                <small className="text-muted">
                                                                    Fulfilled {r.fulfilledAt ? new Date(r.fulfilledAt).toLocaleString() : ''}
                                                                </small>
                                                            )}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        {pagination.totalPages > 1 && (
                                            <nav className="mt-3">
                                                <ul className="pagination justify-content-center">
                                                    <li className={`page-item ${page === 1 ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => fetchRedemptions(page - 1, status)}
                                                            disabled={page === 1}
                                                        >
                                                            Previous
                                                        </button>
                                                    </li>
                                                    {[...Array(pagination.totalPages)].map((_, i) => (
                                                        <li key={i} className={`page-item ${page === i + 1 ? 'active' : ''}`}>
                                                            <button className="page-link" onClick={() => fetchRedemptions(i + 1, status)}>
                                                                {i + 1}
                                                            </button>
                                                        </li>
                                                    ))}
                                                    <li className={`page-item ${page === pagination.totalPages ? 'disabled' : ''}`}>
                                                        <button
                                                            className="page-link"
                                                            onClick={() => fetchRedemptions(page + 1, status)}
                                                            disabled={page === pagination.totalPages}
                                                        >
                                                            Next
                                                        </button>
                                                    </li>
                                                </ul>
                                            </nav>
                                        )}
                                    </>
                                ) : (
                                    <div className="text-center p-5">
                                        <p>Nothing here right now.</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Fulfill modal — single outcome (unlike the receipts review modal),
                so this is just a confirmation + optional note, not a multi-outcome
                selector. note becomes fulfillmentRef: an order/tracking reference
                for a gift_card, or context for a manually-applied fee_credit. */}
            {action && (
                <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Mark redemption fulfilled</h5>
                                <button type="button" className="close" onClick={closeAction}>
                                    <span>&times;</span>
                                </button>
                            </div>
                            <div className="modal-body">
                                <p>
                                    <strong>{action.redemption.userId?.name} {action.redemption.userId?.lastName}</strong>
                                    {' — '}
                                    {formatDollars(action.redemption.dollars)} as {(KIND_LABEL[action.redemption.kind] || action.redemption.kind).toLowerCase()}
                                </p>
                                {action.redemption.fulfillmentStatus === 'failed' && action.redemption.fulfillmentError && (
                                    <div className="alert alert-danger" role="alert">
                                        Auto-apply failed: {action.redemption.fulfillmentError}
                                    </div>
                                )}
                                <div className="form-group">
                                    <label>Note (order/tracking reference, or context — optional)</label>
                                    <textarea
                                        className="form-control"
                                        rows={3}
                                        value={note}
                                        onChange={(e) => setNote(e.target.value)}
                                        placeholder="e.g. Sent Amazon e-gift card #12345, or Applied manually in Stripe dashboard"
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeAction} disabled={submitting}>
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={submitFulfill}
                                    disabled={submitting}
                                >
                                    {submitting ? 'Saving...' : 'Mark fulfilled'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default LoyaltyRedemptions;
